/**
 * useSession.ts
 *
 * Bootstraps the session on app mount:
 *   1. Check localStorage for an existing sessionId
 *   2. If found → call getSession → hydrate store → start subscription + sync
 *   3. If not found (or expired) → call createSession → persist to localStorage
 *
 * This hook runs once at the app root. All other hooks/components consume
 * the already-bootstrapped store.
 */

import { useEffect } from 'react';
import { generateClient } from 'aws-amplify/api';
import { GET_SESSION } from '@/lib/graphql/queries';
import { CREATE_SESSION } from '@/lib/graphql/mutations';
import { useStore } from '@/store';
import { startSessionSync, suppressNextSync } from '@/sync/sessionSync';
import { startSubscription } from '@/sync/subscriptionManager';
import { Session } from '@airline-demo/types';

// Lazy init — generateClient() must be called after Amplify.configure()
let _client: { graphql: Function } | null = null;
const getClient = () => {
  if (!_client) _client = generateClient();
  return _client;
};

const SESSION_KEY = 'airline_demo_session_id';

export function useSession() {
  const { isSessionLoading, sessionId, hydrateFromSession, setSessionLoading, setError } =
    useStore();

  useEffect(() => {
    let syncCleanup: (() => void) | null = null;
    let subCleanup: (() => void) | null = null;

    async function bootstrap() {
      setSessionLoading(true);

      try {
        const storedId = localStorage.getItem(SESSION_KEY);
        let session: Session | null = null;

        if (storedId) {
          try {
            const result = await getClient().graphql({
              query: GET_SESSION,
              variables: { sessionId: storedId },
            });
            session = (result as { data: { getSession: Session } }).data.getSession ?? null;
          } catch {
            // Session expired or network error — create a new one
            session = null;
          }
        }

        if (!session) {
          const result = await getClient().graphql({ query: CREATE_SESSION });
          session = (result as { data: { createSession: Session } }).data.createSession;
          localStorage.setItem(SESSION_KEY, session.sessionId);
        }

        suppressNextSync(); // initial hydration must not trigger an outbound sync
        hydrateFromSession(session);

        // Start real-time subscription BEFORE sync so we don't miss agent updates
        subCleanup = startSubscription(session.sessionId);
        syncCleanup = startSessionSync(session.sessionId);
      } catch (err) {
        console.error('[useSession] bootstrap failed:', err);
        setError('Failed to initialize session. Please refresh.');
        setSessionLoading(false);
      }
    }

    bootstrap();

    return () => {
      syncCleanup?.();
      subCleanup?.();
    };
  }, []);

  return { isSessionLoading, sessionId };
}
